import { useState } from 'react';

import type { SelectableParty } from '@clm/shared-types';

import { PartyPicker, type PartyPickerProps } from './PartyPicker';
import { RegisterVendorForm } from './RegisterVendorForm';

/**
 * The vendor picker — CLM-78 (R-76-2). `PartyPicker` with the request filter
 * `party_type: 'Vendor'` and one footer action, **Register new vendor…**, which
 * opens {@link RegisterVendorForm} inline.
 *
 * Everything FR-14 says about the client picker holds here: the value handed
 * back is a party the API offered (or just registered and returned), never the
 * typed text, and the minted `V…` key is displayed, never editable. Scope still
 * applies underneath (rule 9) — a CLM-registered vendor with no contract yet is
 * selectable by any `contract:upload` holder (A7); from its first contract the
 * normal predicate narrows it.
 */

export type VendorPickerProps = Omit<PartyPickerProps, 'partyType' | 'noMatchLabel'> & {
  /** Hide the register affordance — a filed row, or a surface that only selects. */
  readonly allowRegister?: boolean;
};

export function VendorPicker({
  emptyLabel = 'Search vendors by name or key…',
  allowRegister = true,
  ...picker
}: VendorPickerProps) {
  const [registering, setRegistering] = useState(false);
  /** The party the register form handed back, so the picker renders `display_name (VKEY)` without a search. */
  const [registered, setRegistered] = useState<SelectableParty | null>(null);
  const canRegister = allowRegister && picker.readOnly !== true && picker.disabled !== true;
  const known = registered === null ? picker.initialParties : [registered, ...(picker.initialParties ?? [])];

  return (
    <div className="vp" data-vendor-picker={picker.id}>
      <PartyPicker
        {...picker}
        {...(known === undefined ? {} : { initialParties: known })}
        partyType="Vendor"
        emptyLabel={emptyLabel}
        noMatchLabel="No vendor matches — register it below"
        label={picker.label ?? 'Vendor'}
      />
      {canRegister ? (
        registering ? (
          <RegisterVendorForm
            id={picker.id}
            onRegistered={(party) => {
              setRegistering(false);
              setRegistered(party);
              picker.onChange(party);
            }}
            onCancel={() => {
              setRegistering(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="btn sm qt vp-register"
            aria-expanded={false}
            onClick={() => {
              setRegistering(true);
            }}
          >
            Register new vendor…
          </button>
        )
      ) : null}
    </div>
  );
}
